import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, input, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NGX_ECHARTS_CONFIG, NgxEchartsModule } from 'ngx-echarts';
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { SOCKET_EVENTS, SocketService } from '../../../@core/services/socket.service';
import { ToastService } from '../../../@core/services/toast.service';
import { DurationPipe } from '../../../@shared/pipes/duration.pipe';
import { CheckScreenSize } from '../../../services/check-screen-size.service';
import { IContractInsights } from '../../models/contract-management.model';
import { ContractChecklistService } from '../../services/contract-checklist-item.service';
import { ContractInsightsService } from '../../services/contract-insights.service';
import { ContractTagService, IGetContractTagMappings } from '../../services/contract-tag.service';
import { ContractService } from '../../services/contracts.service';
import { CheckPointsComponent } from '../check-points/check-points.component';
import { CompareHistoryComponent } from "../edit-contract/components/compare/compare-history/compare-history.component";
import { SummaryComponent } from '../summary/summary.component';
import { AiToolingComponent } from '../ai-tooling/ai-tooling.component';
import { KeyHighlightsComponent } from "./components/key-highlights/key-highlights.component";
import { CONTRACT_USER_ROLE } from '../../../@core/models/model';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { AssetUrlPipe } from '../../../@shared/pipes/asset-url.pipe';

enum CURRENT_SCREEN_WINDOW {
  DEFAULT = 'DEFAULT',
  SUMMARY = 'SUMMARY',
  TRANSALTE = 'TRANSLATE',
  CHECKPOINTS = 'CHECKPOINTS',
  KEY_HIGHLIGHTS = 'KEY_HIGHLIGHTS',
  COMPARE = 'COMPARE'
}

@Component({
  selector: 'app-contract-insights',
  imports: [AutoCompleteModule, SelectModule, AccordionModule, BadgeModule, ProgressSpinnerModule, AiToolingComponent, CheckPointsComponent, SummaryComponent, ButtonModule, FormsModule, ButtonModule, CommonModule, NgxEchartsModule,
    ChipModule, TooltipModule, KeyHighlightsComponent, DurationPipe, CompareHistoryComponent, TitleCasePipe, ButtonComponent, AssetUrlPipe],
  providers: [
    {
      provide: NGX_ECHARTS_CONFIG,
      useValue: {
        echarts: () => import('echarts')
      }
    }
  ],
  standalone: true,
  templateUrl: './contract-insights.component.html',
  styleUrl: './contract-insights.component.scss'
})

export class ContractInsightsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() contractId: string = "";
  @Input() documentType!: string;
  @Input() documentName: string = '';
  @Input() isEmptyDoc: boolean = true;
  @Input() tabFocused: boolean = false;
  @Input() currentWalkThroughNumber: number = 0;
  @Output() isEmptyDocChange = new EventEmitter<boolean>();
  @Output() isRefreshingChange = new EventEmitter<boolean>();
  @Input() projectId: string = '';
  @Input() role: string = CONTRACT_USER_ROLE.VIEWER;

  // Track previous values to detect changes
  private previousIsEmptyDoc: boolean = true;
  contractTypes: any[] | undefined;
  contractInsights: IContractInsights | undefined;
  summaryLoading: boolean = true;
  contractTagMappings: IGetContractTagMappings[] = []
  selectedTagId: string = ''
  tagSuggestions: { name: string, _id: string }[] = []
  editTagTypeId: string = ''
  CURRENT_SCREEN_WINDOW = CURRENT_SCREEN_WINDOW
  currentScreenComponent: string = ''
  checklistCount: number = 0 //Save for later checklists count
  isRefreshing: boolean = false
  onEditTag: boolean[] = []
  changedValue: string = ''
  tagsLoading: boolean = false
  refreshInProgressSubscription!: Subscription;
  refreshDoneSubscription!: Subscription;
  refreshFailedSubscription!: Subscription;
  refreshWarningSubscription!: Subscription;
  refreshingUser!: string
  latestInsightDetails: { username: string, updatedAt: Date, createdAt: Date } | null = null
  documentInSync: boolean = false
  suggestionsLoading: boolean = false
  isSmallScreen: boolean = false;
  CONTRACT_USER_ROLE = CONTRACT_USER_ROLE;

  constructor(private contractService: ContractService, private contractInsightsService: ContractInsightsService,
    private contractTagService: ContractTagService, private toast: ToastService,
    private contractChecklistService: ContractChecklistService,
    private checkScreenSizeService: CheckScreenSize,
    private socketService: SocketService, private router: Router, private dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.getLatestInsightsDetails()
    this.refreshInProgressSubscription = this.socketService.on<{ username: string }>(SOCKET_EVENTS.REFRESH_IN_PROGRESS).subscribe(res => {
      if (res.length) {
        this.isRefreshing = true;
        this.isRefreshingChange.emit(true);
        this.refreshingUser = res[0].username
      }
    })
    this.refreshDoneSubscription = this.socketService.on(SOCKET_EVENTS.REFRESH_DONE).subscribe(res => {
      this.isRefreshing = false;
      this.isRefreshingChange.emit(false);
      this.toast.success("Insights generated successfully");
      this.isEmptyDocChange.emit(false);
      this.getLatestInsightsDetails()
    })
    this.refreshFailedSubscription = this.socketService.on(SOCKET_EVENTS.REFRESH_FAILED).subscribe((res: any) => {
      if (res.length) {
        this.toast.error(res[0]?.message)
        this.isEmptyDocChange.emit(true);
      } else {
        this.toast.error("Failed to generate insights. Please try again.")
      };
      this.isRefreshing = false;
      this.isRefreshingChange.emit(false);
      this.getLatestInsightsDetails()
    })

    this.refreshWarningSubscription = this.socketService.on(SOCKET_EVENTS.REFRESH_WARNING).subscribe((res: any) => {
      if (res.length) {
        this.toast.warn(res[0]?.message)
        this.isEmptyDocChange.emit(false);
      } else {
        this.toast.warn("Failed to generate insights. Please try again.")
      };
      this.isRefreshing = false;
      this.isRefreshingChange.emit(false);
      this.getLatestInsightsDetails()
    })
    this.isSmallScreen = this.checkScreenSizeService.checkScreenSize()
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.getSaveForLaterChecklistsCount()
    if ((changes['tabFocused'] && changes['tabFocused'].currentValue)) {
      this.currentScreenComponent = '';
    }

    // Debug: Log when isEmptyDoc changes
    if (changes['isEmptyDoc']) {
      this.previousIsEmptyDoc = changes['isEmptyDoc'].currentValue;
    }
  }

  ngOnDestroy(): void {
    if (this.refreshInProgressSubscription) {
      this.refreshInProgressSubscription.unsubscribe()
    }
    if (this.refreshDoneSubscription) {
      this.refreshDoneSubscription.unsubscribe()
    }
    if (this.refreshFailedSubscription) {
      this.refreshFailedSubscription.unsubscribe()
    }
    if (this.refreshWarningSubscription) {
      this.refreshWarningSubscription.unsubscribe()
    }
  }

  generateInsights() {
    this.isRefreshing = true;
    this.isRefreshingChange.emit(true);
    if (this.contractId.length > 0) {
      this.contractInsightsService.generateEmbeddings(this.contractId).subscribe({
        next: (res) => {
        },
        error: (err) => {
          this.toast.error("Failed to generate insights. Please try again.");
          console.error("Error generating embeddings:", err);
          this.isRefreshing = false;
          this.isRefreshingChange.emit(false);
        }
      });
    }
    else {
      console.error("No such contract")
    }
  }


  generateSummary(generateInsights: boolean) {
    if (this.contractId.length > 0) {
      this.summaryLoading = true;
      this.contractInsightsService.generateSummary(this.contractId).subscribe({
        next: (res: any) => {
          if (res.data) {
            this.contractInsights = res.data;
          }
          this.summaryLoading = false;
        },
        error: (error) => {
          this.summaryLoading = false;
        }
      });
    }
  }

  searchTags(event: any, tagTypeId: string) {
    this.suggestionsLoading = true
    this.contractTagService.getTagsBasedOnSearchValue({ contract: this.contractId, tagType: tagTypeId, searchValue: event.query }).subscribe(res => {
      if (res.success && res.data) {
        this.tagSuggestions = res.data.map((tag: { _id: string; name: string }) => ({
          ...tag,
          name: this.trimLetters(tag.name, 35)
        }));
        this.suggestionsLoading = false
      }
    })
  }

  trimLetters(text: string, charLimit: number): string {
    if (text.length > charLimit) {
      return text.slice(0, charLimit) + '...';
    }
    return text;
  }

  onOptionSelect(event: AutoCompleteSelectEvent) {
    this.selectedTagId = event.value._id
  }

  addTagMapping(tagTypeId: string) {
    this.contractTagService.addContractTagMapping({ contract: this.contractId, tagType: tagTypeId, tag: this.selectedTagId }).subscribe(res => {
      if (res.success) {
        this.selectedTagId = ''
        this.editTagTypeId = ''
      }
    })
  }

  editClicked(tagTypeId: string) {
    this.selectedTagId = ''
    this.editTagTypeId = this.editTagTypeId == tagTypeId ? '' : tagTypeId
    let i = this.contractTagMappings.findIndex(tag => tag.tagTypeId == tagTypeId)
    if (this.contractTagMappings[i].dataType == 'Custom') {
      return
    }
    this.contractTagMappings[i].tags?.forEach(tag => {
      if (tag.aiGenerated || tag.dataType != 'Custom') {
        this.editTagTypeId = ''
      }
    })
    this.onEditTag[i] = !this.onEditTag[i]
  }

  deleteClicked(tagTypeId: string, tagId: string) {
    this.contractTagService.removeContractTagMapping({ contract: this.contractId, tagType: tagTypeId, tag: tagId }).subscribe(res => { })
  }

  goBack() {
    if (this.currentScreenComponent == CURRENT_SCREEN_WINDOW.CHECKPOINTS) {
      this.getSaveForLaterChecklistsCount()
    }
    this.currentScreenComponent = '';
  }

  openComponent(currentScreen: string) {
    this.currentScreenComponent = currentScreen
  }

  getSaveForLaterChecklistsCount() {
    this.contractChecklistService.getSaveForLaterChecklistsCount(this.contractId).subscribe(res => {
      if (res.success) {
        this.checklistCount = res.data
      }
    })
  }

  suggestEdit(tagTypeId: string, tagId: string, close: boolean) {
    if (close) {
      this.tagsLoading = true
      this.editTagTypeId = ''
      this.changedValue = ''
      return
    }
    this.tagsLoading = true
    if (this.changedValue == '') {
      this.tagsLoading = false
      return
    }
    this.contractTagService.addAItagToContractTagMapping({ contract: this.contractId, tagType: tagTypeId, tag: tagId, tagName: this.changedValue }).subscribe(res => { })
  }

  getLatestInsightsDetails() {
    this.contractService.getLatestInsightsDetails(this.contractId).subscribe(res => {
      if (res.success) {
        if (res.data.inProgressJob) {
          this.isRefreshing = true
          this.isRefreshingChange.emit(true);
          this.refreshingUser = res.data.inProgressJob.username
        }
        else if (res.data.latestInsights) {
          this.latestInsightDetails = res.data.latestInsights
        }
      }
    })
  }

}
